// frontend/modules/customer-loan-info/hooks/useCustomerLoanInfo.ts
import { useQueryClient } from '@tanstack/react-query';
import { 
  useGetCustomerLoanInformation,
  useGetLoanSummary,
  useGetPaymentHistory,
  useGetUpcomingPayments,
  useHasActiveLoan,
  useGetBasicLoanInfo
} from '@/shared/api/client/customer-loan-information-controller';
import { useCustomerLoanInfoStore } from '../stores/customer-loan-info-store';
import { CustomerLoanSearchParams } from '../types';

export const useCustomerLoanInfo = () => {
  const queryClient = useQueryClient();
  const { 
    searchParams, 
    isLoading, 
    error, 
    setSearchParams, 
    setLoading, 
    setError, 
    clearError 
  } = useCustomerLoanInfoStore();

  // Main customer loan information query
  const customerLoanQuery = useGetCustomerLoanInformation(
    searchParams?.branch || '',
    searchParams?.custNo || '',
    searchParams?.glNo || '',
    searchParams?.transactionDate ? { transactionDate: searchParams.transactionDate } : undefined,
    {
      query: {
        enabled: !!(searchParams?.branch && searchParams?.custNo && searchParams?.glNo),
        refetchOnWindowFocus: false,
      }
    }
  );

  // Loan summary query
  const loanSummaryQuery = useGetLoanSummary(
    searchParams?.branch || '',
    searchParams?.custNo || '',
    searchParams?.glNo || '',
    searchParams?.transactionDate ? { transactionDate: searchParams.transactionDate } : undefined,
    {
      query: {
        enabled: !!(searchParams?.branch && searchParams?.custNo && searchParams?.glNo),
        refetchOnWindowFocus: false,
      }
    }
  );

  // Payment history query
  const paymentHistoryQuery = useGetPaymentHistory(
    searchParams?.branch || '',
    searchParams?.custNo || '',
    searchParams?.glNo || '',
    undefined,
    {
      query: {
        enabled: !!(searchParams?.branch && searchParams?.custNo && searchParams?.glNo),
        refetchOnWindowFocus: false,
      }
    }
  );

  // Upcoming payments query
  const upcomingPaymentsQuery = useGetUpcomingPayments(
    searchParams?.branch || '',
    searchParams?.custNo || '',
    searchParams?.glNo || '',
    undefined,
    {
      query: {
        enabled: !!(searchParams?.branch && searchParams?.custNo && searchParams?.glNo),
        refetchOnWindowFocus: false,
      }
    }
  );

  // Active loan check query
  const activeLoanQuery = useHasActiveLoan(
    searchParams?.branch || '',
    searchParams?.custNo || '',
    searchParams?.glNo || '',
    {
      query: {
        enabled: !!(searchParams?.branch && searchParams?.custNo && searchParams?.glNo),
        refetchOnWindowFocus: false,
      }
    }
  );

  // Basic loan info query
  const basicLoanInfoQuery = useGetBasicLoanInfo(
    searchParams?.branch || '',
    searchParams?.custNo || '',
    searchParams?.glNo || '',
    searchParams?.transactionDate ? { transactionDate: searchParams.transactionDate } : undefined,
    {
      query: {
        enabled: !!(searchParams?.branch && searchParams?.custNo && searchParams?.glNo),
        refetchOnWindowFocus: false,
      }
    }
  );

  const searchCustomerLoan = (params: CustomerLoanSearchParams) => {
    clearError();
    setSearchParams(params);
  };

  const clearSearch = () => {
    setSearchParams(null);
    clearError();
  };

  const refetchAll = () => {
    if (searchParams?.branch && searchParams?.custNo && searchParams?.glNo) {
      customerLoanQuery.refetch();
      loanSummaryQuery.refetch();
      paymentHistoryQuery.refetch();
      upcomingPaymentsQuery.refetch();
      activeLoanQuery.refetch();
      basicLoanInfoQuery.refetch();
    }
  };

  return {
    // State
    searchParams,
    isLoading: customerLoanQuery.isLoading || loanSummaryQuery.isLoading,
    error: customerLoanQuery.error || loanSummaryQuery.error || error,
    
    // Data
    customerLoanInfo: customerLoanQuery.data,
    loanSummary: loanSummaryQuery.data,
    paymentHistory: paymentHistoryQuery.data,
    upcomingPayments: upcomingPaymentsQuery.data,
    hasActiveLoan: activeLoanQuery.data,
    basicLoanInfo: basicLoanInfoQuery.data,
    
    // Query states
    isCustomerLoanLoading: customerLoanQuery.isLoading,
    isLoanSummaryLoading: loanSummaryQuery.isLoading,
    isPaymentHistoryLoading: paymentHistoryQuery.isLoading,
    isUpcomingPaymentsLoading: upcomingPaymentsQuery.isLoading,
    isActiveLoanLoading: activeLoanQuery.isLoading,
    isBasicLoanInfoLoading: basicLoanInfoQuery.isLoading,
    
    // Actions
    searchCustomerLoan,
    clearSearch,
    refetchAll,
    clearError,
  };
};
